import { Box, Grid2 as Grid, Typography, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/system";
import { CheckBoxField } from "@pimo/pimo-components/src/lib/molecules";
import { DropdownFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/fields/dropdown-form-field";
import { FormFieldWrapper } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field-wrapper";
import {
  Initiative,
  PRIMARY_IMPACT_DRIVERS,
  QUALITATIVE_IMPACT_TYPES,
} from "aint-types";
import {
  FLOAT_REGEX,
  formatDateUTC,
  getImpactRangeForDates,
  IMPACT_SERIES_IDS_AND_LABELS,
  LARGE_SCREEN_BREAKPOINT,
} from "aint-utils";
import dayjs from "dayjs";
import { useFormContext, useWatch } from "react-hook-form";

import { EditDialogHeadline } from "../../../components/edit-dialog-headline/edit-dialog-headline";
import { INDateFormField } from "../../../components/form-fields/in-date-form-field/in-date-form-field";
import { INFormFieldInfoSlot } from "../../../components/form-fields/in-form-field-info-slot";
import { ImpactPlanSection } from "../../../components/impact-plan-section/impact-plan-section";
import { InfoSlot } from "../../../components/info-slot/info-slot";
import { OptionalLabelText } from "../../../components/optional-label-text";
import { FORM_FIELD_INFO_TEXT } from "../../../form/form-field-info-text";
import { RichTextFormField } from "../../../form/form-fields/rich-text-field";
import { SelectField } from "../../../form/form-fields/select-field";
import { TextFormField } from "../../../form/form-fields/text-field";

const SIGN_OFF_OPTIONS = ["Yes", "No", "N/A"];

export function EditInitiativeImpactTab(props: {
  isOnCreate: boolean;
  activeInitiativeUID: string;
}) {
  const [startDate, monitoringEndDate, applicableImpactSeries] = useWatch({
    name: ["startDate", "monitoringEndDate", "applicableImpactSeries"],
  });
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(LARGE_SCREEN_BREAKPOINT);

  const impactRange =
    !startDate || !monitoringEndDate
      ? []
      : getImpactRangeForDates(
          formatDateUTC(startDate, "", "dayjs") as dayjs.Dayjs,
          dayjs(monitoringEndDate)
        );

  const flexDirection = isLargeScreen ? "row" : "column";
  const wrapperXs = isLargeScreen ? 4 : 12;

  return (
    <Grid
      container
      flexDirection="column"
      gap="12px"
      key="impact-tab"
      sx={{ marginTop: theme.spacing(3) }}
      size={12}
    >
      <EditDialogHeadline sx={{ fontSize: "22px" }}>
        Initiative Timeline
      </EditDialogHeadline>
      <Grid gap="12px" display="flex" flexDirection={flexDirection} size={12}>
        <Grid size={wrapperXs}>
          <FormFieldWrapper
            label="Project Start date*"
            infoSlot={
              <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["startDate"]} />
            }
          >
            <INDateFormField name="startDate" views={["month", "year"]} />
          </FormFieldWrapper>
        </Grid>

        <Grid size={wrapperXs}>
          <FormFieldWrapper
            label="Project End date*"
            infoSlot={
              <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["endDate"]} />
            }
          >
            <INDateFormField
              name="endDate"
              views={["month", "year"]}
              setEndOfMonth={true}
            />
          </FormFieldWrapper>
        </Grid>
        <Grid size={wrapperXs}>
          <FormFieldWrapper
            label="Monitoring End date*"
            infoSlot={
              <INFormFieldInfoSlot
                text={FORM_FIELD_INFO_TEXT["monitoringEndDate"]}
              />
            }
          >
            <INDateFormField
              name="monitoringEndDate"
              views={["month", "year"]}
              setEndOfMonth={true}
            />
          </FormFieldWrapper>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <EditDialogHeadline sx={{ fontSize: "22px" }}>
          Monthly Impact Metrics
        </EditDialogHeadline>
        <InfoSlot>
          <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["impactSection"]} />
        </InfoSlot>
      </Box>
      <ImpactSeriesSelection />
      {IMPACT_SERIES_IDS_AND_LABELS.map((series) => {
        if (!(applicableImpactSeries ?? []).includes(series.id)) {
          return;
        }
        return (
          <ImpactPlanSectionForSeries
            key={`impact-plan-section-for-${series.id}-${series.label}${props.isOnCreate ? "create" : "edit"}-${props.activeInitiativeUID}${window.location.pathname}`}
            series={series}
            activeInitiativeUID={
              props.isOnCreate
                ? Date.now().toString()
                : props.activeInitiativeUID
            }
            impactRange={impactRange}
            isOnCreate={props.isOnCreate}
          />
        );
      })}
      <OtherImpactMetricsSection />
    </Grid>
  );
}

function ImpactSeriesSelection() {
  const { register, watch } = useFormContext();
  const applicableImpactSeries = watch("applicableImpactSeries");

  return (
    <FormFieldWrapper label="Applicability of Monthly Impact Metric">
      <DropdownFormField
        multiSelect
        register={register}
        type="dropdown"
        name="applicableImpactSeries"
        items={IMPACT_SERIES_IDS_AND_LABELS.map((series) => ({
          label: series.label,
          value: series.id,
        }))}
      />
      {(applicableImpactSeries ?? []).includes("tieServiceExpensesImpact") && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <CheckBoxField name="tieServiceExpensesIncludesHrCosts" label="" />
          <span style={{ marginLeft: "-18px", marginBottom: "-2px" }}>
            TIE + Service Expenses Impact <b>includes HR Expenses</b>
          </span>
        </Box>
      )}
    </FormFieldWrapper>
  );
}

function OtherImpactMetricsSection() {
  const [primaryImpactDriver, qualitativeImpactType] = useWatch({
    name: ["primaryImpactDriver", "qualitativeImpactType"],
  }) as [
    Initiative["primaryImpactDriver"],
    Initiative["qualitativeImpactType"],
  ];

  const currentYear = dayjs().year();
  const baselineYearOptions = [];
  let year = 2020;

  while (year <= currentYear) {
    baselineYearOptions.push(year.toString());
    year++;
  }

  return (
    <>
      <EditDialogHeadline sx={{ fontSize: "22px" }}>
        Other Impact Metrics
      </EditDialogHeadline>
      <Grid>
        <FormFieldWrapper
          label="Primary Impact Driver*"
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["primaryImpactDriver"]}
            />
          }
        >
          <SelectField
            name="primaryImpactDriver"
            options={PRIMARY_IMPACT_DRIVERS.map((type) => type)}
            multiple
          />
        </FormFieldWrapper>
      </Grid>
      {primaryImpactDriver?.includes("Qualitative") && (
        <>
          <Grid>
            <FormFieldWrapper label="Qualitative Impact Type*">
              <SelectField
                name="qualitativeImpactType"
                options={QUALITATIVE_IMPACT_TYPES.map((type) => type)}
              />
            </FormFieldWrapper>
          </Grid>
          {qualitativeImpactType === "Other" && (
            <Grid>
              <FormFieldWrapper label="Qualitative Impact Type Other*">
                <TextFormField name="qualitativeImpactTypeOther" />
              </FormFieldWrapper>
            </Grid>
          )}
        </>
      )}
      <Grid>
        <FormFieldWrapper
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, fontSize: "14px" }}
              gutterBottom
            >
              One Off Investment (in k Eur){" "}
              <OptionalLabelText>(Optional)</OptionalLabelText>
            </Typography>
          }
          infoSlot={
            <INFormFieldInfoSlot
              text={FORM_FIELD_INFO_TEXT["oneOffInvestment"]}
            />
          }
        >
          <TextFormField name="oneOffInvestment" valueRegex={FLOAT_REGEX} />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Baseline Year*"
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["baselineYear"]} />
          }
        >
          <SelectField name="baselineYear" options={baselineYearOptions} />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Sign off (COO)*"
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["signOffCoo"]} />
          }
        >
          <SelectField name="signOffCoo" options={SIGN_OFF_OPTIONS} />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Sign off (CFO)*"
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["signOffCfo"]} />
          }
        >
          <SelectField name="signOffCfo" options={SIGN_OFF_OPTIONS} />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Sign off (CHRO)*"
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["signOffChro"]} />
          }
        >
          <SelectField name="signOffChro" options={SIGN_OFF_OPTIONS} />
        </FormFieldWrapper>
      </Grid>
      <Grid>
        <FormFieldWrapper
          label="Comment"
          infoSlot={
            <INFormFieldInfoSlot text={FORM_FIELD_INFO_TEXT["impactComment"]} />
          }
        >
          <RichTextFormField name="impactComment" />
        </FormFieldWrapper>
      </Grid>
    </>
  );
}

function ImpactPlanSectionForSeries({
  series,
  activeInitiativeUID,
  impactRange,
  isOnCreate,
}: {
  series: {
    id: string;
    label: string;
  };
  activeInitiativeUID?: string;
  impactRange: { year: string; months: string[] }[];
  isOnCreate: boolean;
}) {
  const currentYear = dayjs().year();
  const theme = useTheme();

  return (
    <Box key={`-${activeInitiativeUID}`}>
      <EditDialogHeadline sx={{ margin: 0 }}>
        {series.label +
          (series.id.toLowerCase().includes("fte")
            ? " (in # of FTE)"
            : " (in k EUR)")}
      </EditDialogHeadline>
      <Typography
        color={theme.palette.grey[700]}
        fontSize="10px"
        paddingTop="5px"
        paddingBottom="10px"
      >
        All Monthly Impact Metrics figures are cumulative
      </Typography>
      {impactRange.map(({ year, months }) => {
        return (
          <ImpactPlanSection
            key={`${series.id}-${series.label}-${year}-${activeInitiativeUID}`}
            activeInitiativeUID={activeInitiativeUID ?? ""}
            year={year}
            months={months}
            seriesId={series.id}
            seriesLabel={`${series.label} ${series.id.includes("Fte") ? "(in # FTE)" : "(in k EUR)"}`}
            lockPlanValues={!isOnCreate && +year < currentYear}
          />
        );
      })}
    </Box>
  );
}
